@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import com.royceecoleman.bill.auth.authenticatedFetch
import com.royceecoleman.bill.auth.canAccessAdminDashboard
import com.royceecoleman.bill.auth.fetchAuthenticatedSession
import com.royceecoleman.bill.billing.approveAllWorkspaceEntries
import com.royceecoleman.bill.billing.approveWorkspaceEntry
import com.royceecoleman.bill.billing.createWorkspace
import com.royceecoleman.bill.billing.deleteBillingClient
import com.royceecoleman.bill.billing.deleteBillingProfile
import com.royceecoleman.bill.billing.deleteProfileExample
import com.royceecoleman.bill.billing.deleteWorkspace
import com.royceecoleman.bill.billing.downloadBillingFile
import com.royceecoleman.bill.billing.exportWorkspace
import com.royceecoleman.bill.billing.generateWorkspace
import com.royceecoleman.bill.billing.getProfileExamples
import com.royceecoleman.bill.billing.getWorkbench
import com.royceecoleman.bill.billing.getWorkspace
import com.royceecoleman.bill.billing.getWorkspaceHistory
import com.royceecoleman.bill.billing.redraftWorkspaceEntry
import com.royceecoleman.bill.billing.refreshBillingAccess
import com.royceecoleman.bill.billing.saveBillingClient
import com.royceecoleman.bill.billing.saveBillingProfile
import com.royceecoleman.bill.billing.saveEntryAsExample
import com.royceecoleman.bill.billing.saveWorkspace
import com.royceecoleman.bill.billing.subscriptionLink
import com.royceecoleman.bill.shared.billing.BillingCodes
import com.royceecoleman.bill.shared.billing.BillingEntry
import com.royceecoleman.bill.shared.billing.BillingExample
import com.royceecoleman.bill.shared.billing.BillingGuide
import com.royceecoleman.bill.shared.billing.BillingProfile
import com.royceecoleman.bill.shared.billing.BillingWorkspace
import com.royceecoleman.bill.shared.billing.InvoiceDetails
import com.royceecoleman.bill.shared.billing.MAX_ABOUT_LENGTH
import com.royceecoleman.bill.shared.billing.WorkspaceCreate
import com.royceecoleman.bill.shared.billing.WorkspaceHome
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Blockquote
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.events.Event
import org.w3c.files.File
import kotlin.js.Promise
import kotlin.js.unsafeCast

internal val LocalWorkbenchBusy = staticCompositionLocalOf { false }

/** A blank profile for the editor. New profiles start without task and activity codes; stored ones keep their setting. */
internal fun newBillingProfile() = BillingProfile(examples = listOf(BillingExample(), BillingExample()), includeCodes = false)

@Composable
fun BillingWorkbenchPage() {
    val scope = rememberCoroutineScope()
    var home by remember { mutableStateOf<WorkspaceHome?>(null) }
    var page by remember { mutableStateOf("home") }
    var user by remember { mutableStateOf("") }
    var isAdmin by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var notice by remember { mutableStateOf("") }
    // A short confirmation pinned to the bottom of the screen, visible wherever the page is scrolled.
    var toast by remember { mutableStateOf("") }
    LaunchedEffect(toast) {
        if (toast.isNotEmpty()) {
            delay(3000)
            toast = ""
        }
    }
    var dirty by remember { mutableStateOf(false) }
    var workspace by remember { mutableStateOf<BillingWorkspace?>(null) }
    var profile by remember { mutableStateOf(newBillingProfile()) }
    // Previous bills picked for a profile that is not saved yet; uploaded when it is saved, dropped when the page is left.
    var pendingBills by remember { mutableStateOf(PreviousBillQueue<File>()) }
    var helpOpen by remember { mutableStateOf(false) }
    var templateInvoice by remember { mutableStateOf(InvoiceDetails()) }
    var templateClient by remember { mutableStateOf("" to "") }

    fun run(action: suspend () -> Unit) {
        if (busy) return
        busy = true
        error = ""
        notice = ""
        scope.launch {
            try {
                action()
            } catch (
                cancel: CancellationException,
            ) {
                throw cancel
            } catch (
                failure: Exception,
            ) {
                error = failure.message ?: "Something went wrong. Your saved drafts are safe."
            } finally {
                busy = false
            }
        }
    }

    fun navigate(target: String) {
        if (target == page) return
        if (busy || (dirty && !window.confirm("Leave this page and discard unsaved edits?"))) return
        page = target
        dirty = false
        pendingBills = PreviousBillQueue()
        error = ""
        notice = ""
    }

    suspend fun reloadHome() {
        home = getWorkbench()
    }

    LaunchedEffect(page) { focusBillingHeading() }

    fun openWorkspace(id: String) =
        run {
            workspace = getWorkspace(id)
            dirty = false
            page = "review"
        }

    LaunchedEffect(Unit) {
        document.title = "BILL · Legal billing, in your words"
        run {
            val session = fetchAuthenticatedSession()
            if (session == null) {
                window.location.href = "/login"
            } else {
                user = session.username
                isAdmin = session.role.canAccessAdminDashboard()
                reloadHome()
                if (window.location.search.contains("payment=returned")) {
                    home = home?.copy(access = refreshBillingAccess())
                    page = "plan"
                    notice = "Payment status checked with Stripe. Your plan is shown below."
                }
            }
        }
    }
    DisposableEffect(dirty) {
        val handler: (Event) -> Unit = { event ->
            if (dirty) {
                event.preventDefault()
                event.asDynamic().returnValue = ""
            }
        }
        window.addEventListener("beforeunload", handler)
        onDispose { window.removeEventListener("beforeunload", handler) }
    }
    LaunchedEffect(workspace?.id, workspace?.status) {
        val current = workspace
        if (current?.status == "GENERATING") {
            while (true) {
                delay(3000)
                try {
                    workspace = getWorkspace(current.id)
                    if (workspace?.status != "GENERATING") {
                        reloadHome()
                        break
                    }
                } catch (cancel: CancellationException) {
                    throw cancel
                } catch (
                    _: Exception,
                ) {
                    error = "Connection interrupted. Your notes are saved. Use Reload to check drafting status."
                    break
                }
            }
        }
    }

    DisposableEffect(Unit) {
        val stylesheet = document.createElement("link")
        stylesheet.setAttribute("rel", "stylesheet")
        stylesheet.setAttribute("href", "/bill-workbench.css")
        document.head?.appendChild(stylesheet)
        onDispose { stylesheet.remove() }
    }
    CompositionLocalProvider(LocalWorkbenchBusy provides busy) {
        Div({ classes("bill-app") }) {
            Header({ classes("bill-top") }) {
                Div({ classes("bill-brand") }) {
                    Text("BILL")
                    Span { Text("LESS ADMIN. MORE PRACTICE.") }
                }
                Div({ classes("bill-row") }) {
                    Span({ classes("bill-muted") }) { Text(user) }
                    home?.access?.let { access -> Span({ classes("bill-pill") }) { Text("${access.plan} · ${access.remaining} credits") } }
                    BillButton(if (helpOpen) "Close Ask BILL" else "Ask BILL", busy) { helpOpen = !helpOpen }
                    if (isAdmin) A(href = "/dashboard") { Text("Administration") }
                    BillButton("Sign out", busy) {
                        if (!dirty || window.confirm("Sign out and discard unsaved edits?")) {
                            run {
                                val response = authenticatedFetch("/api/auth/logout", "POST")
                                check(response.ok) { "Could not sign out. Please retry." }
                                dirty = false
                                window.location.href = "/login"
                            }
                        }
                    }
                }
            }
            Div({ classes("bill-layout") }) {
                Nav({
                    classes("bill-nav")
                    attr("aria-label", "Billing navigation")
                }) {
                    listOf(
                        "home" to "Overview & history",
                        "new" to "New monthly workspace",
                        "profiles" to "Examples & client rules",
                        "rules" to "Standing rules",
                        "clients" to "Clients & matters",
                        "plan" to "Plan & subscription",
                        "help" to "Quick-start guide",
                        "support" to "Feedback & support",
                        "demo" to "Try a sample",
                    ).forEach { (target, label) ->
                        BillButton(label, busy, if (page == target) "active" else "") { navigate(target) }
                    }
                    P { Text("Your examples. Your voice. Your final say.") }
                }
                Main({
                    classes("bill-main")
                    attr("aria-busy", busy.toString())
                }) {
                    if (error.isNotEmpty()) {
                        Div({
                            classes("bill-alert")
                            attr("role", "alert")
                        }) { Text(error) }
                    }
                    if (notice.isNotEmpty()) {
                        Div({
                            classes("bill-alert", "bill-notice")
                            attr("role", "status")
                        }) { Text(notice) }
                    }
                    // Always present so screen readers announce the toast when it appears.
                    Div({ attr("aria-live", "polite") }) {
                        if (toast.isNotEmpty()) Div({ classes("bill-toast") }) { Text(toast) }
                    }
                    if (busy) {
                        P({
                            attr("role", "status")
                            classes("bill-muted")
                        }) { Text("Working… Please keep this page open. Saved drafts can be resumed from history.") }
                    }
                    val data = home
                    if (data == null) {
                        H1 { Text("A calmer close to the month.") }
                        P { Text("Loading your private billing workspace…") }
                        if (!busy) BillButton("Retry") { run { reloadHome() } }
                    } else {
                        if (helpOpen && page in BillingGuide.askScreens) {
                            key(page, workspace?.id) {
                                AskBillPanel(page, if (page == "review") workspace else null, dirty, data.profiles.isNotEmpty()) {
                                    helpOpen = false
                                    window.setTimeout({ focusBillingHeading() }, 0)
                                }
                            }
                        }
                        when (page) {
                            "help" -> QuickStartGuide { navigate(if (data.profiles.isEmpty()) "profiles" else "new") }
                            "support" -> SupportPanel()
                            "demo" -> SampleWalkthrough { navigate(if (data.profiles.isEmpty()) "profiles" else "new") }
                            "home" -> {
                                Div({ classes("bill-hero") }) {
                                    Div {
                                        Div({ classes("bill-eyebrow") }) { Text("FROM ROUGH NOTES TO REVIEWED ENTRIES") }
                                        H1 { Text("Close the month.\nKeep your voice.") }
                                        P({
                                            classes("bill-muted")
                                        }) {
                                            Text(
                                                "Teach BILL with a few before-and-after examples. Add this month's work. Review clear narratives, with billing codes if your client uses them, then export to your billing software.",
                                            )
                                        }
                                        BillButton(
                                            if (data.profiles.isEmpty()) "Start with your examples →" else "Start a monthly workspace →",
                                            busy,
                                            "primary",
                                        ) {
                                            if (data.profiles.isEmpty()) {
                                                profile = newBillingProfile()
                                                navigate("profile")
                                            } else {
                                                navigate("new")
                                            }
                                        }
                                        P { Text("First time? Try a sample from the menu. No client data, AI calls or credits.") }
                                    }
                                    Div({ classes("bill-example") }) {
                                        Div({ classes("bill-eyebrow") }) { Text("ILLUSTRATIVE EXAMPLE · NOT YOUR DATA") }
                                        Blockquote { Text("“rev opposing MSJ re causation”") }
                                        P { Text("↓ Your examples guide the wording") }
                                        P { Text("Review and analyze opposing counsel's motion for summary judgment regarding causation.") }
                                        Span({ classes("bill-pill") }) { Text("L240 · A104 · Lawyer review required") }
                                    }
                                }
                                Div({ classes("bill-grid") }) {
                                    Stat("${data.profiles.size}", "Reusable client profiles")
                                    Stat("${data.workspaces.count { it.status == "REVIEW" }}", "Workspaces awaiting review")
                                    Stat("${data.access.remaining}", "Drafting credits remaining")
                                }
                                H2 { Text("Your workspaces") }
                                if (data.workspaces.isEmpty()) {
                                    Div({ classes("bill-card", "bill-empty") }) {
                                        H3 { Text("Your next month starts here.") }
                                        P({
                                            classes("bill-muted")
                                        }) {
                                            Text(
                                                "Save a client profile with two or more paired examples, then add your notes. Nothing is sent to AI until you choose Draft.",
                                            )
                                        }
                                    }
                                } else {
                                    Div({ classes("bill-card", "bill-scroll") }) {
                                        Table({ classes("bill-list") }) {
                                            Thead {
                                                Tr {
                                                    listOf(
                                                        "Workspace",
                                                        "Month",
                                                        "Status",
                                                        "Reviewed",
                                                        "",
                                                    ).forEach { Th { Text(it) } }
                                                }
                                            }
                                            Tbody {
                                                data.workspaces.forEach { row ->
                                                    Tr {
                                                        Td { Text(row.title) }
                                                        Td { Text(row.month) }
                                                        Td { Span({ classes("bill-pill") }) { Text(row.status) } }
                                                        Td { Text("${row.approved} / ${row.entries}") }
                                                        Td { BillButton("Open", busy) { openWorkspace(row.id) } }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            "profiles" -> {
                                Eyebrow("01 / TEACH BILL YOUR STYLE")
                                H1 { Text("Good examples do the teaching.") }
                                P({
                                    classes("bill-muted")
                                }) {
                                    Text(
                                        "Pair the notes you actually wrote with billing entries your client accepted. BILL uses these examples in each draft; it does not fine-tune a model or share profiles between accounts.",
                                    )
                                }
                                Div({ classes("bill-row") }) {
                                    BillButton("New profile", busy) {
                                        profile = newBillingProfile()
                                        page = "profile"
                                    }
                                }
                                data.profiles.forEach { p ->
                                    Div({ classes("bill-card") }) {
                                        Div({ classes("bill-row", "bill-between") }) {
                                            Div {
                                                H3 { Text(p.name) }
                                                val stats = data.profileStats.firstOrNull { it.profileId == p.id }
                                                Text(
                                                    "${p.examples.size} examples + ${stats?.learnedExamples ?: 0} learned · ${p.incrementMinutes}-minute increments · Revision ${p.revision}",
                                                )
                                                if (stats != null && stats.approvedDrafts > 0) {
                                                    P({ classes("bill-muted") }) {
                                                        Text(
                                                            "Draft quality: ${stats.approvedDrafts} approved drafts · ${stats.unchanged * 100 / stats.approvedDrafts}% approved unchanged · ${(stats.meanSimilarity * 100).toInt()}% average wording kept" +
                                                                if (p.includeCodes) " · codes kept ${stats.taskCodeKept * 100 / stats.approvedDrafts}% / ${stats.activityCodeKept * 100 / stats.approvedDrafts}%" else "",
                                                        )
                                                    }
                                                }
                                            }
                                            BillButton("Edit profile", busy) {
                                                profile = p
                                                page = "profile"
                                            }
                                        }
                                    }
                                }
                                if (data.profiles.isEmpty()) {
                                    P({
                                        classes("bill-card")
                                    }) { Text("No profiles yet. Start with two examples from one client or matter type.") }
                                }
                            }
                            "profile" ->
                                ProfileEditor(profile, { profile }, busy, pendingBills, { update ->
                                    pendingBills = update(pendingBills)
                                    dirty = true
                                }, {
                                    profile = it
                                    dirty = true
                                }, {
                                    run {
                                        val firstSetup = profile.id.isEmpty()
                                        val saved = saveBillingProfile(profile)
                                        val queued = pendingBills.files
                                        pendingBills = PreviousBillQueue()
                                        // The editor keeps showing the unsaved profile until its queued bills are read.
                                        val uploads = uploadPreviousBills(queued, { notice = it }) { uploadPickedBill(saved.id, it) }
                                        profile = saved
                                        dirty = false
                                        reloadHome()
                                        page = if (firstSetup) "new" else "profiles"
                                        val read = uploads.saved.size
                                        notice =
                                            "Profile saved.${if (read > 0) " $read previous ${if (read == 1) "bill" else "bills"} read." else ""} ${if (firstSetup) "Next: add this month's notes. You can reuse this profile every month." else "Existing workspaces keep their original profile snapshot."}"
                                        if (uploads.failures.isNotEmpty()) {
                                            error = "These previous bills could not be read. Edit the profile to upload them again.\n${uploads.failures.joinToString("\n")}"
                                        }
                                    }
                                }, {
                                    if (window.confirm("Delete this reusable profile? Existing workspace snapshots will remain.")) {
                                        run {
                                            deleteBillingProfile(profile.id)
                                            dirty =
                                                false
                                            reloadHome()
                                            page = "profiles"
                                        }
                                    }
                                })
                            "rules" ->
                                RulesPage(
                                    busy,
                                    { action -> run(action) },
                                    { dirty = it },
                                    { notice = it },
                                )
                            "clients" ->
                                ClientsPage(
                                    data.clients,
                                    data.profiles,
                                    busy,
                                    { dirty = true },
                                    save = { client, onSaved ->
                                        run {
                                            saveBillingClient(client)
                                            dirty = false
                                            reloadHome()
                                            onSaved()
                                            notice = "Client saved. Its details fill in when you start a monthly workspace."
                                        }
                                    },
                                    delete = { client ->
                                        if (window.confirm("Delete ${client.name}? Existing workspaces keep their invoice details.")) {
                                            run {
                                                deleteBillingClient(client.id)
                                                reloadHome()
                                            }
                                        }
                                    },
                                    startWorkspace = { client, matter ->
                                        templateInvoice = applyClient(InvoiceDetails(), client, matter.id)
                                        templateClient = client.id to matter.id
                                        profile = data.profiles.firstOrNull { it.id == matter.profileId.ifBlank { client.defaultProfileId } } ?: profile
                                        navigate("new")
                                    },
                                )
                            "new" ->
                                NewWorkspaceForm(
                                    data.profiles,
                                    data.clients,
                                    busy,
                                    { dirty = true },
                                    profile.id,
                                    templateInvoice,
                                    templateClient.first,
                                    templateClient.second,
                                ) { request ->
                                    run {
                                        workspace = createWorkspace(request)
                                        dirty = false
                                        reloadHome()
                                        page = "review"
                                        notice =
                                            "Notes saved. Choose Draft with AI when you are ready to send them to your configured provider."
                                    }
                                }
                            "review" ->
                                workspace?.let { current ->
                                    ReviewWorkspace(
                                        current,
                                        { requireNotNull(workspace) },
                                        busy,
                                        dirty,
                                        ReviewActions(
                                            change = {
                                                workspace = it
                                                dirty = true
                                            },
                                            save = {
                                                run {
                                                    workspace = saveWorkspace(requireNotNull(workspace))
                                                    dirty = false
                                                    reloadHome()
                                                    notice = "Edits saved. Changed entries need fresh approval."
                                                }
                                            },
                                            reload = {
                                                if (!dirty || window.confirm("Discard unsaved edits and reload?")) openWorkspace(current.id)
                                            },
                                            generate = {
                                                if (window.confirm(
                                                        "Send ${current.notes.size} notes and the most relevant examples from this profile to the configured AI provider? Each note that is drafted uses one credit (up to ${current.notes.size}); notes that cannot be drafted are not charged. Only submit information you are authorized to share.",
                                                    )
                                                ) {
                                                    run {
                                                        workspace = generateWorkspace(current)
                                                        dirty = false
                                                        reloadHome()
                                                    }
                                                }
                                            },
                                            approve = { id ->
                                                if (window.confirm(
                                                        "I have checked this item against the original note or receipt, actual time${if (current.profile.includeCodes) ", billing codes" else ""}, all warnings and the client's guidelines. I approve it for billing.",
                                                    )
                                                ) {
                                                    run {
                                                        workspace = approveWorkspaceEntry(current, id)
                                                        reloadHome()
                                                    }
                                                }
                                            },
                                            approveAll = {
                                                if (window.confirm(
                                                        "Approve every entry and expense that has no blocking issue? I confirm I have checked each against its original note, actual time${if (current.profile.includeCodes) ", billing codes" else ""}, all warnings and the client's guidelines. Items with blocking issues are skipped.",
                                                    )
                                                ) {
                                                    run {
                                                        val result = approveAllWorkspaceEntries(current)
                                                        workspace = result.workspace
                                                        reloadHome()
                                                        notice = "${result.approved} approved.${if (result.skipped > 0) " ${result.skipped} skipped because of blocking issues." else ""}"
                                                    }
                                                }
                                            },
                                            redraft = { id, hint ->
                                                if (window.confirm("Redraft entry $id with AI? One credit is used only if a new draft is returned.")) {
                                                    run {
                                                        workspace = redraftWorkspaceEntry(current, id, hint)
                                                        reloadHome()
                                                        notice = "Entry $id redrafted. Review it before approving."
                                                    }
                                                }
                                            },
                                            saveExample = { id ->
                                                run {
                                                    saveEntryAsExample(current, id)
                                                    reloadHome()
                                                    notice = "Saved to the ${current.profile.name} profile's examples. Future drafts can learn from it."
                                                }
                                            },
                                            loadHistory = { onLoaded -> run { onLoaded(getWorkspaceHistory(current.id)) } },
                                            export = { format ->
                                                run {
                                                    downloadBillingFile(exportWorkspace(current, format))
                                                    workspace = getWorkspace(current.id)
                                                    notice = "Approved entries downloaded. Check your billing software's import mapping before submission."
                                                }
                                            },
                                            delete = {
                                                if (window.confirm(
                                                        "Permanently delete this workspace, original notes and review history from BILL? Download any records you need first. Credits already used are not refunded. Examples learned from it stay with the profile.",
                                                    )
                                                ) {
                                                    run {
                                                        deleteWorkspace(current)
                                                        workspace = null
                                                        dirty = false
                                                        reloadHome()
                                                        page = "home"
                                                    }
                                                }
                                            },
                                            copyAll = {
                                                val text = entriesClipboardText(requireNotNull(workspace))
                                                scope.launch {
                                                    try {
                                                        window.navigator.clipboard
                                                            .writeText(text)
                                                            .await()
                                                        toast = "Outputs copied to clipboard."
                                                    } catch (failure: Throwable) {
                                                        if (failure is CancellationException) throw failure
                                                        error = "Your browser did not allow copying to the clipboard. Allow clipboard access for this site and try again."
                                                    }
                                                }
                                            },
                                            nextMonth = {
                                                if (!dirty || window.confirm("Discard unsaved edits and start another month?")) {
                                                    templateInvoice = current.invoice.copy(month = "", invoiceNumber = "", invoiceDate = "", description = "", workDate = "")
                                                    templateClient = current.clientRecordId to current.matterRecordId
                                                    profile = current.profile
                                                    dirty = false
                                                    page = "new"
                                                    notice =
                                                        "Matter details copied. Enter the new month and verify the rate. New work uses the latest saved profile; originals stay in history."
                                                }
                                            },
                                        ),
                                    )
                                }
                            "plan" -> {
                                Eyebrow("A SMALLER MONTH-END ADMIN BURDEN")
                                H1 { Text("Your billing assistant.\nYour subscription.") }
                                Div({ classes("bill-two") }) {
                                    Div({ classes("bill-card") }) {
                                        H2 { Text(data.access.plan) }
                                        val added = data.access.addedCredits
                                        Stat(
                                            "${data.access.remaining - added} / ${data.access.limit}" + if (added > 0) " + $added added" else "",
                                            "Drafting credits available",
                                        )
                                        P { Text(data.access.description) }
                                        if (data.access.resetDate.isNotEmpty()) P { Text("Next reset: ${data.access.resetDate} UTC") }
                                    }
                                    Div({ classes("bill-card") }) {
                                        H2 { Text("BILL Pro") }
                                        P {
                                            Text(
                                                "Monthly drafting allowance, reusable client profiles, review tools, and CSV / LEDES exports. The configured plan's price and renewal terms appear on Stripe's secure checkout before you subscribe.",
                                            )
                                        }
                                        if (!data.access.paymentsConfigured) {
                                            P({
                                                classes("bill-alert", "bill-notice")
                                            }) {
                                                Text(
                                                    "Paid subscriptions are not available on this installation yet. Your trial and saved work remain available.",
                                                )
                                            }
                                        }
                                        Div({ classes("bill-row") }) {
                                            BillButton(
                                                "View price & subscribe",
                                                busy ||
                                                    !data.access.paymentsConfigured ||
                                                    data.access.plan in listOf("PRO", "COMPLIMENTARY"),
                                                "primary",
                                            ) {
                                                run {
                                                    window.location.href =
                                                        subscriptionLink("checkout").url
                                                }
                                            }
                                            if (data.access.hasCustomer) {
                                                BillButton("Manage subscription", busy || !data.access.paymentsConfigured) {
                                                    run {
                                                        window.location.href =
                                                            subscriptionLink("portal").url
                                                    }
                                                }
                                            }
                                            BillButton("Refresh payment status", busy) {
                                                run {
                                                    home = data.copy(access = refreshBillingAccess())
                                                    notice =
                                                        "Payment status refreshed."
                                                }
                                            }
                                        }
                                    }
                                }
                                P({
                                    classes("bill-muted")
                                }) {
                                    Text(
                                        "One credit covers one note drafted by AI, or one entry redrafted. Notes that cannot be drafted are not charged. Editing, approving and exporting saved work require no credits.",
                                    )
                                }
                            }
                        }
                    }
                    Footer({ classes("bill-footer") }) {
                        Text(
                            "BILL is a drafting assistant, not a guarantee of legal or client compliance. Review all work before billing. Notes, examples, your profile's About you text, previous bills and your standing rules are sent to your configured AI provider only when you draft, upload a previous bill or chat with BILL about your rules. Ask BILL separately sends your question and a numeric progress summary with consent. Confirm your firm's confidentiality and provider-retention requirements before submitting client information.",
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ProfileEditor(
    p: BillingProfile,
    latest: () -> BillingProfile,
    busy: Boolean,
    pendingBills: PreviousBillQueue<File>,
    changePendingBills: ((PreviousBillQueue<File>) -> PreviousBillQueue<File>) -> Unit,
    change: (BillingProfile) -> Unit,
    save: () -> Unit,
    delete: () -> Unit,
) {
    var step by remember(p.id) { mutableStateOf(1) }
    var stepError by remember { mutableStateOf("") }
    LaunchedEffect(step) { focusBillingHeading() }
    Eyebrow("EXAMPLES & CLIENT RULES")
    H1 { Text(if (p.id.isEmpty()) "Build your first billing profile." else "Refine your billing profile.") }
    GuideSteps(step)
    if (step == 1) {
        H2 { Text("Show us how you write.") }
        P {
            Text(
                "A profile saves your writing examples and client rules so you only set them up once. Choose two different tasks with entries your client accepted. Remove unnecessary confidential details.",
            )
        }
        BillField("Profile name", p.name, "e.g. Acme — commercial litigation") { change(latest().copy(name = it)) }
        BillArea(
            "About you (optional)",
            p.about,
            "e.g. I am a litigator defending insurance carriers' insureds. My bills must satisfy the carriers' billing guidelines, so each entry shows the legal purpose of the work.",
        ) { change(latest().copy(about = it.take(MAX_ABOUT_LENGTH))) }
        P({ classes("bill-muted") }) {
            Text(
                "Describe yourself, your role and who you bill for this profile (up to 4,000 characters). BILL gives this to the AI every time it drafts or redrafts with this profile; edits apply to the next draft.",
            )
        }
    }
    if (step == 2) {
        H2 { Text("Tell us your client's requirements.") }
        P {
            Text(
                "Use the client's written billing guidelines. The defaults are starting values, not confirmation that your client accepts them.",
            )
        }
        Div({ classes("bill-card") }) {
            BillArea(
                "Client billing instructions",
                p.instructions,
                "Tone, prohibited work, required detail, abbreviation rules. These instructions assist drafting; review is still required.",
            ) {
                change(latest().copy(instructions = it))
            }
            Div({ classes("bill-grid") }) {
                BillSelect(
                    "Billing increment (minutes)",
                    p.incrementMinutes.toString(),
                    listOf("1", "3", "6", "15").associateWith {
                        it
                    },
                ) { change(latest().copy(incrementMinutes = it.toInt())) }
                P { Text("For example, 6 minutes is 0.1 hours. This checks your recorded time; BILL never rounds it up.") }
            }
            BillDetails("Advanced wording limits") {
                BillField("Maximum narrative characters", p.maxNarrativeLength.toString()) {
                    change(
                        latest().copy(
                            maxNarrativeLength =
                                it.toIntOrNull() ?: 0,
                        ),
                    )
                }
                BillField("Prohibited wording (comma-separated)", p.prohibitedTerms.joinToString(", ")) {
                    change(latest().copy(prohibitedTerms = it.split(',').map(String::trim).filter(String::isNotEmpty)))
                }
            }
        }
    }
    if (step == 1) {
        H2 { Text("Before → after") }
        P({ classes("bill-muted") }) { Text("Use 2–20 examples. Remove client details that are not needed to teach the writing style.") }
        latest().examples.forEachIndexed { index, example ->
            Div({ classes("bill-card") }) {
                H3 { Text("Example ${index + 1}") }
                Div({ classes("bill-two") }) {
                    BillArea("Your rough note", example.before, "rev opposing MSJ re causation") { value ->
                        change(
                            latest().copy(
                                examples =
                                    latest().examples.mapIndexed { i, e ->
                                        if (i ==
                                            index
                                        ) {
                                            e.copy(before = value)
                                        } else {
                                            e
                                        }
                                    },
                            ),
                        )
                    }
                    BillArea(
                        "Accepted billing entry",
                        example.after,
                        "Review and analyze opposing counsel's motion for summary judgment regarding causation.",
                    ) { value ->
                        change(
                            latest().copy(
                                examples =
                                    latest().examples.mapIndexed { i, e ->
                                        if (i ==
                                            index
                                        ) {
                                            e.copy(after = value)
                                        } else {
                                            e
                                        }
                                    },
                            ),
                        )
                    }
                }
                if (latest().includeCodes) {
                    Div({ classes("bill-two") }) {
                        fun setCodes(transform: (BillingExample) -> BillingExample) =
                            change(latest().copy(examples = latest().examples.mapIndexed { i, e -> if (i == index) transform(e) else e }))
                        BillSelect("Task code (optional)", example.taskCode, BillingCodes.tasks.filterKeys { it in latest().taskCodes }, true) { code ->
                            setCodes { it.copy(taskCode = code) }
                        }
                        BillSelect("Activity code (optional)", example.activityCode, BillingCodes.activities.filterKeys { it in latest().activityCodes }, true) { code ->
                            setCodes { it.copy(activityCode = code) }
                        }
                    }
                }
                if (latest().examples.size >
                    2
                ) {
                    BillButton("Remove example", busy) {
                        change(
                            latest().copy(
                                examples =
                                    latest().examples.filterIndexed {
                                        i,
                                        _,
                                        ->
                                        i != index
                                    },
                            ),
                        )
                    }
                }
            }
        }
        BillButton("+ Add another example", busy || latest().examples.size >= 20) {
            change(
                latest().copy(
                    examples =
                        latest().examples + BillingExample(),
                ),
            )
        }
        PreviousBills(p.id, busy, pendingBills, changePendingBills)
        if (stepError.isNotBlank()) {
            P({
                attr("role", "alert")
                classes("bill-alert")
            }) { Text(stepError) }
        }
        BillButton("Continue to client requirements →", busy, "primary") {
            if (latest().name.isBlank() || latest().examples.any { it.before.isBlank() || it.after.isBlank() }) {
                stepError = "Name this profile and complete both sides of every example before continuing."
            } else {
                stepError = ""
                step = 2
            }
        }
    }
    if (step == 2) {
        Div({ classes("bill-card") }) {
            H2 { Text("Billing codes") }
            BillCheck("Include task and activity codes (UTBMS)", p.includeCodes) { change(latest().copy(includeCodes = it)) }
            P({ classes("bill-muted") }) {
                Text(
                    if (p.includeCodes) {
                        "Drafts suggest a task and activity code for each entry, and exports include them."
                    } else {
                        "Turn this on only if the client requires codes. Without codes, drafts, review and exports leave them out."
                    },
                )
            }
        }
        if (p.includeCodes) {
            BillDetails("Review client-permitted billing codes") {
                H2 { Text("Client-permitted billing codes") }
                P({
                    classes("bill-muted")
                }) {
                    Text(
                        "The initial code set is ABA litigation tasks and ABA activities. Uncheck codes this client does not accept. Other practice-area code sets are not supported in this release.",
                    )
                }
                Div({ classes("bill-two") }) {
                    CodeChoices("Task codes", BillingCodes.tasks, { latest().taskCodes }) { change(latest().copy(taskCodes = it)) }
                    CodeChoices(
                        "Activity codes",
                        BillingCodes.activities,
                        { latest().activityCodes },
                    ) { change(latest().copy(activityCodes = it)) }
                }
            }
        }
        Div({ classes("bill-card") }) {
            H2 { Text("Learning from your approvals") }
            BillCheck("When I approve an AI draft I rewrote, save my wording as an example for this profile", p.learnFromApprovals) {
                change(latest().copy(learnFromApprovals = it))
            }
            P({ classes("bill-muted") }) {
                Text("Learned examples contain the original note and your approved narrative. They stay private to your account and are sent to the AI only as examples when drafting for this profile.")
            }
            if (p.id.isNotEmpty()) LearnedExamples(p.id, busy)
        }
        val queued = pendingBills.files.size
        if (queued > 0) {
            P({ classes("bill-muted") }) { Text("$queued previous ${if (queued == 1) "bill" else "bills"} will be read when you save this profile.") }
        }
        Div({ classes("bill-row") }) {
            BillButton("← Back to examples", busy) { step = 1 }
            BillButton("Save profile", busy, "primary", save)
            if (p.id.isNotEmpty()) BillButton("Delete profile", busy, "danger", delete)
        }
    }
}

@Composable
internal fun BillButton(
    label: String,
    disabled: Boolean = false,
    kind: String = "",
    action: () -> Unit,
) {
    Button(attrs = {
        classes("bill-button")
        if (kind.isNotEmpty()) classes(kind)
        attr("type", "button")
        if (disabled) attr("disabled", "")
        onClick { action() }
    }) { Text(label) }
}

@Composable
internal fun BillField(
    label: String,
    value: String,
    placeholder: String = "",
    change: (String) -> Unit,
) {
    val disabled = LocalWorkbenchBusy.current
    Label(attrs = { classes("bill-field") }) {
        Text(label)
        Input(InputType.Text, attrs = {
            if (disabled) attr("disabled", "")
            value(value)
            attr("placeholder", placeholder)
            onInput { change(it.value) }
        })
    }
}

@Composable
internal fun BillArea(
    label: String,
    value: String,
    placeholder: String = "",
    kind: String = "",
    change: (String) -> Unit,
) {
    val disabled = LocalWorkbenchBusy.current
    Label(attrs = { classes("bill-field") }) {
        Text(label)
        TextArea(value = value, attrs = {
            if (disabled) attr("disabled", "")
            if (kind.isNotEmpty()) classes(kind)
            attr("placeholder", placeholder)
            onInput { change(it.value) }
        })
    }
}

@Composable
internal fun BillSelect(
    label: String,
    value: String,
    options: Map<String, String>,
    allowEmpty: Boolean = false,
    change: (String) -> Unit,
) {
    val disabled = LocalWorkbenchBusy.current
    Label(attrs = { classes("bill-field") }) {
        Text(label)
        Select(attrs = {
            attr("aria-label", label)
            if (disabled) attr("disabled", "")
            onChange { change(it.value.orEmpty()) }
        }) {
            if (allowEmpty || value !in options) Option("", attrs = { if (value !in options) attr("selected", "") }) { Text("Select…") }
            options.forEach { (key, text) ->
                Option(key, attrs = {
                    if (key ==
                        value
                    ) {
                        attr("selected", "")
                    }
                }) { Text(if (key == text || key.length > 20) text else "$key — $text") }
            }
        }
    }
}

@Composable
internal fun BillCheck(
    label: String,
    checked: Boolean,
    change: (Boolean) -> Unit,
) {
    val disabled = LocalWorkbenchBusy.current
    Label(attrs = { classes("bill-check") }) {
        Input(InputType.Checkbox, attrs = {
            if (disabled) attr("disabled", "")
            checked(checked)
            onChange { change(it.value) }
        })
        Text(label)
    }
}

@Composable
private fun CodeChoices(
    label: String,
    options: Map<String, String>,
    selected: () -> List<String>,
    change: (List<String>) -> Unit,
) {
    Div {
        H3 { Text(label) }
        Div({ classes("bill-code-options") }) {
            options.forEach { (code, text) ->
                BillCheck(
                    "$code — $text",
                    code in selected(),
                ) { checked -> change(if (checked) selected() + code else selected() - code) }
            }
        }
    }
}

@Composable
private fun Stat(
    value: String,
    label: String,
) {
    Div({ classes("bill-card") }) {
        Div({ classes("bill-stat") }) { Text(value) }
        Text(label)
    }
}

@Composable
internal fun Eyebrow(text: String) {
    Div({ classes("bill-eyebrow") }) { Text(text) }
}

@Composable
internal fun BillDetails(
    title: String,
    content: @Composable () -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    Div({ classes("bill-card") }) {
        Button(attrs = {
            classes("bill-button")
            attr("type", "button")
            attr("aria-expanded", open.toString())
            onClick { open = !open }
        }) { Text("${if (open) "−" else "+"} $title") }
        if (open) content()
    }
}

@Composable
private fun LearnedExamples(
    profileId: String,
    busy: Boolean,
) {
    var examples by remember(profileId) { mutableStateOf<List<BillingExample>?>(null) }
    var error by remember(profileId) { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    LaunchedEffect(profileId) {
        try {
            examples = getProfileExamples(profileId)
        } catch (cancel: CancellationException) {
            throw cancel
        } catch (e: Exception) {
            error = e.message.orEmpty()
        }
    }
    val list = examples
    BillDetails("Learned examples (${list?.size ?: "…"})") {
        if (error.isNotBlank()) P({ classes("bill-alert") }) { Text(error) }
        if (list != null && list.isEmpty()) P({ classes("bill-muted") }) { Text("None yet. Approve drafts you rewrote, or choose Save as example on an entry.") }
        list?.forEach { example ->
            Div({ classes("bill-card") }) {
                P({ classes("bill-source") }) { Text(example.before) }
                P { Text("→ ${example.after}") }
                Div({ classes("bill-row", "bill-between") }) {
                    Span({ classes("bill-muted") }) {
                        Text(listOf(example.taskCode, example.activityCode, if (example.origin == "SAVED") "saved by you" else "learned from an approval").filter(String::isNotBlank).joinToString(" · "))
                    }
                    BillButton("Delete", busy, "danger") {
                        if (window.confirm("Delete this learned example? Future drafts will no longer use it.")) {
                            scope.launch {
                                try {
                                    deleteProfileExample(profileId, example.id)
                                    examples = examples?.filterNot { it.id == example.id }
                                } catch (cancel: CancellationException) {
                                    throw cancel
                                } catch (e: Exception) {
                                    error = e.message.orEmpty()
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
